import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { query, where } from 'firebase/firestore';
import { Column } from 'react-rainbow-components';
import useWebhooks from 'data/firestore/webhooks/useCollection';
import Button from 'components/Button';
import Table from 'components/Table';
import DateTimeColumn from 'components/DateTimeColumn';
import SearchIcon from 'components/icons/search';
import Pagination from 'components/Pagination';
import { Webhooks } from 'data/firestore/webhooks/types';
import useCustomSearchParams from 'hooks/useCustomSearchParams';
import useSearchFilters from 'hooks/useSearchFilters';
import { PAGE_SIZE } from '../../../constants';
import Empty from './empty';
import {
    Container,
    SearchInput,
    RightContent,
    StyledCard,
    TableContainer,
    PaginationContainer,
    EntriesText,
    SearchContainer,
    StyledBadgeOverlay,
} from './styled';
import Name from './columns/name';
import ActionsColumn from './columns/actions';
import useManageWebhooks from '../hooks/useManageWebhooks';
import checkFilters from './helpers/checkFilters';
import FilterButton from './FilterButton';

const dataSearchFilters = ['search', 'events'];

const WebhooksPage = () => {
    const { agentId = '' } = useParams<{ agentId: string }>();

    const {
        enable, disable, remove, edit, create,
    } = useManageWebhooks({ agentId });

    const {
        values: filterValues,
        getValue: getFilterValue,
        setValue: setFilterValue,
    } = useSearchFilters({
        filterNames: dataSearchFilters,
    });

    const activeFiltersCount = useMemo(
        () => dataSearchFilters.reduce(
            (count, key) => {
                if (key === 'search') return count;
                return (
                    getFilterValue(key) !== null
                        ? count + 1
                        : count
                );
            },
            0,
        ),
        [getFilterValue],
    );

    const [searchParams, setSearchParams] = useCustomSearchParams({
        page: {
            type: 'number',
            default: 1,
        },
        event: {
            type: 'string',
            default: null,
        },
    });

    const { page: activePage } = searchParams;

    const { data: webhooks = [], isLoading } = useWebhooks({
        disabled: !agentId,
        listQueryFn: (ref) => query(ref, where('group', '==', agentId)),
        track: [agentId],
    });

    const filteredData = useMemo(
        () => webhooks.filter((webhook) => checkFilters({
            value: webhook,
            filterValues,
            filterNames: dataSearchFilters,
        })),
        [webhooks, filterValues],
    );

    const isEmptyList = !isLoading && webhooks.length === 0;

    const handleToggleEnabled = useCallback((webhook: Webhooks) => {
        const callableFn = webhook.status ? disable : enable;
        callableFn(webhook.id);
    }, [disable, enable]);

    const handleDelete = useCallback((webhook: Webhooks) => remove(webhook.id), [remove]);

    const handlePageChange = (event: React.ChangeEvent<unknown>, pageNumber: number) => {
        if (isLoading) return;
        setSearchParams({ page: pageNumber });
    };

    useEffect(() => {
        setSearchParams({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredData.length]);

    const dataToShow = filteredData;
    const totalHits = dataToShow.length;
    const pages = Math.ceil(totalHits / PAGE_SIZE);
    const firstItemOfActivePage = dataToShow.length === 0 ? 0 : PAGE_SIZE * (activePage - 1) + 1;
    const lastItemOfActivePage = Math.min(
        PAGE_SIZE * activePage,
        (PAGE_SIZE * activePage - PAGE_SIZE) + dataToShow.length,
    );

    if (isEmptyList) {
        return (
            <Container>
                <Empty onClick={create} />
            </Container>
        );
    }

    return (
        <StyledCard>
            <SearchContainer>
                <SearchInput
                    type="search"
                    placeholder="Search webhooks..."
                    variant="bare"
                    icon={<SearchIcon />}
                    borderRadius="semi-rounded"
                    onChange={(e) => setFilterValue('search', e.target.value || null)}
                    value={getFilterValue('search') || ''}
                />
                <RightContent>
                    <StyledBadgeOverlay
                        value={activeFiltersCount}
                        isHidden={activeFiltersCount === 0}
                        variant="brand"
                        position="top-left"
                    >
                        <FilterButton label="Filters" />
                    </StyledBadgeOverlay>
                    <Button
                        label="Create New Webhook"
                        variant="brand"
                        size="medium"
                        borderRadius="semi-rounded"
                        onClick={() => create()}
                    />
                </RightContent>
            </SearchContainer>
            <TableContainer>
                <Table
                    data={isLoading ? [] : dataToShow}
                    isLoading={isLoading}
                    keyField="id"
                >
                    <Column
                        header="Webhook"
                        field="id"
                        component={Name}
                    />
                    <Column
                        header="Created At"
                        field="createdAt"
                        width={200}
                        component={DateTimeColumn}
                    />
                    <Column
                        field="id"
                        component={ActionsColumn}
                        width={200}
                        onToggle={handleToggleEnabled}
                        onEdit={edit}
                        onDelete={handleDelete}
                    />
                </Table>
                <PaginationContainer>
                    <EntriesText>
                        Showing
                        {' '}
                        {firstItemOfActivePage}
                        {' '}
                        to
                        {' '}
                        {lastItemOfActivePage}
                        {' '}
                        of
                        {' '}
                        {totalHits}
                        {' '}
                        entries.
                    </EntriesText>
                    <Pagination
                        activePage={activePage}
                        pages={pages}
                        onChange={handlePageChange}
                    />
                </PaginationContainer>
            </TableContainer>
        </StyledCard>
    );
};

export default WebhooksPage;
